import Head from "next/head";
import { connect } from "react-redux";

import Heading from "@components/Heading";
import AppleCategoryCard from "@components/ShopInShop/Apple/AppleCategoryCard";
import AppleNavbar from "@components/ShopInShop/Apple/AppleNavbar";
import ResponsiveShopBanner from "@components/ShopInShop/ResponsiveShopBanner";
import ErrorPage from "@containers/ErrorPage";
import { getShopInShopCategoriesApi } from "@lib/api";
import { makeApiCall } from "@lib/api/helpers";
import { trackProductClick } from "@store/tracker/actions";
import FullWidthContainer from "@components/FullWidthContainer";
import VideoBanner from "@components/ShopInShop/VideoBanner";

const AppleSubcategories = ({ category, menu, subcategories }) => {
  if (!category) {
    return <ErrorPage layout="404" />;
  }

  const hasTopBanner =
    category.banners &&
    category.banners.find((banner) => banner.position === "top");

  const hasTopVideoBanner =
    category.banners &&
    category.banners.find(
      (banner) =>
        banner.position === "top" &&
        banner.type === "video" &&
        banner.video?.url
    );

  return (
    <>
      <Head>
        {category.seo?.title && <title>{category.seo.title}</title>}
        {category.seo?.description && (
          <meta name="description" content={category.seo.description} />
        )}
      </Head>

      {menu && (
        <FullWidthContainer>
          <div className="bg-[#F5F5F7]">
            <nav className="flex items-center text-center gap-[50px] md:gap-[60px] py-[15px] md:py-[24px] container">
              <AppleNavbar menu={menu} />
            </nav>
          </div>
        </FullWidthContainer>
      )}

      {hasTopVideoBanner && (
        <FullWidthContainer>
          <div className="bg-black">
            <div className="md:container md:w-[80%] lg:w-full mx-auto">
              <VideoBanner banner={hasTopVideoBanner} />
            </div>
          </div>
        </FullWidthContainer>
      )}

      {hasTopBanner && !hasTopVideoBanner && (
        <FullWidthContainer padding="md:px-[15px]">
          <div className="md:rounded-[6px] overflow-hidden">
            <ResponsiveShopBanner banner={hasTopBanner} />
          </div>
        </FullWidthContainer>
      )}

      {subcategories && (
        <div className="mt-[20px] md:mt-[40px]">
          {category.name && (
            <div className="mb-[10px]">
              <Heading as="h1" title={category.name} />
            </div>
          )}

          <ul className="grid gap-[10px] md:gap-[20px] grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
            {subcategories.map((subcategory) => (
              <li
                key={subcategory.slug}
                className="flex flex-col bg-[#FAFAFA] rounded-[6px] px-[10px] py-[8px] md:p-[20px]"
              >
                <AppleCategoryCard category={subcategory} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

AppleSubcategories.pageLayout = "default";

AppleSubcategories.getInitialProps = async (ctx) => {
  const { query } = ctx;

  const response = await makeApiCall({
    apiCall: getShopInShopCategoriesApi,
    apiArgs: [{ shopName: "apple", shopCategories: query.params }],
    returnResponse: true,
    formatResponse: (response) => response.data,
  });

  if (response) {
    const updatedResponse = { ...response };

    if (response.menu?.length > 0) {
      updatedResponse.menu = response.menu.map((menuItem) => ({
        ...menuItem,
        // in menu we highlight the main subcategory.
        // ex: /shop/shopName/mainCategory/subcategory1/subcategory2
        // we need to know that we are in the mainCategory. query.params[0] is the mainCategory
        isActive: menuItem.url.includes(`/${query.params[0]}`),
      }));
    } else {
      updatedResponse.menu = null;
    }

    if (response.category?.children?.length > 0) {
      updatedResponse.subcategories = response.category.children;
    } else {
      updatedResponse.subcategories = null;
    }

    return {
      ...updatedResponse,
      query,
    };
  }

  return { query };
};

export default connect(null, { trackProductClick })(AppleSubcategories);
